import { useState, useEffect } from 'react'
import { OptimizelyExperiment, OptimizelyFeaturesMap } from '@optimizely/optimizely-sdk/lib/shared_types'
import { enums } from '@optimizely/react-sdk'
import { useOptimizelyContext } from '@/contexts/OptimizelyContext'

export interface PickableOptimizelyExperiment {
  experimentKey: string
  variations: string[]
  enabledVariation: string | undefined
}

export function useOptimizelyFeatureList(): {
  experiments: PickableOptimizelyExperiment[]
  forceExperimentVariation: (experimentId: string, variation: string) => void
  isExperimentSelected: (experimentId: string, variation: string) => boolean
  configVersion: number
} {
  const [configVersion, setConfigVersion] = useState<number>(0)
  const [experiments, setExperiments] = useState<PickableOptimizelyExperiment[]>([])
  const { optimizely } = useOptimizelyContext()
  const updateConfigVersion = () => setConfigVersion((currentConfigVersion) => currentConfigVersion + 1)

  useEffect(() => {
    const listenerId = optimizely.notificationCenter.addNotificationListener(
      enums.NOTIFICATION_TYPES.OPTIMIZELY_CONFIG_UPDATE,
      updateConfigVersion,
    )

    return () => {
      optimizely.notificationCenter.removeNotificationListener(listenerId)
    }
  }, [optimizely?.notificationCenter])

  const returnEnabledVariation = (experiment: OptimizelyExperiment) => {
    for (const variationKey in experiment.variationsMap) {
      if (experiment.variationsMap[variationKey].featureEnabled) {
        return variationKey
      }
    }
  }

  useEffect(() => {
    const mappedExperiments: PickableOptimizelyExperiment[] = []
    const featuresMap: OptimizelyFeaturesMap | undefined = optimizely.getOptimizelyConfig()?.featuresMap
    featuresMap &&
      Object.keys(featuresMap).forEach((featureId) => {
        featuresMap[featureId]?.experimentRules?.forEach((experiment: OptimizelyExperiment) => {
          mappedExperiments.push({
            experimentKey: experiment.key,
            variations: Object.keys(experiment.variationsMap),
            enabledVariation: returnEnabledVariation(experiment),
          })
        })
      })
    setExperiments(mappedExperiments)
  }, [optimizely, configVersion])

  const forceExperimentVariation = (experimentKey: string, variation: string) => {
    optimizely.setForcedVariation(experimentKey, variation)
    updateConfigVersion()
  }

  const isExperimentSelected = (experimentKey: string, variation: string) => {
    return optimizely.getVariation(experimentKey) === variation
  }

  return {
    experiments,
    forceExperimentVariation,
    isExperimentSelected,
    configVersion,
  }
}
